import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Router } from '@angular/router';
import { from, of } from 'rxjs';
import { catchError, map, mergeMap, switchMap, tap } from 'rxjs/operators';
import * as Action from './actions';
import * as fromFermenter from './reducer';
import { FermenterService } from 'app/services/dal/fermenter.service';
import { ToastService } from 'app/services/toast.service';
import { shouldLoadData, trackLoading } from '../effect-helpers';

@Injectable()
export class FermenterEffects {

  constructor(
    private router: Router,
    private actions$: Actions,
    private store: Store,
    private fermenterService: FermenterService,
    private toastService: ToastService) { }

  getItems$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(Action.loadItems),
      shouldLoadData(
        this.store,
        'fermenters',
        (action) => action.barId,
        fromFermenter.selectAllItems,
      ),
      switchMap((action) =>
        trackLoading(
          this.store,
          'fermenters',
          action.barId,
          this.fermenterService.getItems(action.barId).pipe(
            map(items => Action.loadItemsSuccess({ items })),
            catchError(error => of(Action.loadItemsFailure({ error })))
          )
        )
      )
    );
  });

  setItemFromListPage$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(Action.setItemFromListPage, Action.setItemAndNavigate),
      tap((action) => {
        this.router.navigate(['/inventory/fermenter/details', action.id]);
      }),
    );
  }, { dispatch: false });

  update$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(Action.updateItem),
      mergeMap((action) => from(this.fermenterService.update(action.item)).pipe(
        map(() => {
          this.toastService.saveSuccess();
          return Action.updateItemSuccess({ item: action.item });
        }),
        catchError(error => of(Action.updateItemFailure({ error })))
      ))
    );
  });

  add$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(Action.addItem),
      mergeMap((action) => from(this.fermenterService.add(action.item, action.id)).pipe(
        map(() => {
          this.toastService.saveSuccess();
          return Action.addItemSuccess({ item: action.item });
        }),
        catchError(error => of(Action.addItemFailure({ error })))
      ))
    );
  });

  delete$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(Action.deleteItem),
      mergeMap((action) => from(this.fermenterService.delete(action.id)).pipe(
        map(() => {
          this.toastService.deleteItemSuccess();
          return Action.deleteItemSuccess();
        }),
        catchError(error => of(Action.deleteItemFailure({ error })))
      ))
    );
  });

  softUpdate$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(Action.softUpdate),
      mergeMap((action) => from(this.fermenterService.softUpdate(action.id, action.data, action.uid)).pipe(
        map(() => Action.softUpdateSuccess({ id: action.id, data: action.data })),
        catchError(error => of(Action.softUpdateFailure({ error })))
      ))
    );
  });

  softUpdateSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(Action.softUpdateSuccess),
      tap(() => this.toastService.saveSuccess()),
    );
  }, { dispatch: false });

  handleFailure$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(Action.loadItemsFailure, Action.addItemFailure, Action.updateItemFailure, Action.deleteItemFailure, Action.softUpdateFailure),
      tap(({ error }) => this.toastService.error(JSON.stringify(error))),
    );
  }, { dispatch: false });
}
